/**
 * Cleans pasted text before line classification.
 *
 * Column positions matter (a chord sits over the lyric at the same column),
 * so every rewrite here is either whole-line or keeps one character per
 * character.
 */
const ZERO_WIDTH = /[\u00AD\u200B-\u200D\u2060\uFEFF]/g;
// Ideographic space (U+3000) is kept: it is two columns wide, like the CJK text around it.
const SPACE_LOOKALIKES = /[\u00A0\u1680\u2000-\u200A\u202F\u205F]/g;
// Tabs are expanded first; any other control character is noise from the copy.
const CONTROL_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;
// Chord-site markup copied along with the text: [ch]G[/ch], [tab]...[/tab]
const CHORD_SITE_MARKUP = /\[\/?(?:ch|tab)\]/gi;
const TAB_STOP = 8;
const DASHES = /[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g;
const SINGLE_QUOTES = /[\u2018-\u201B\u2032]/g;
const DOUBLE_QUOTES = /[\u201C-\u201F\u2033]/g;
const expandTabs = (line) => {
    if (!line.includes('\t'))
        return line;
    let out = '';
    for (const ch of line) {
        out += ch === '\t' ? ' '.repeat(TAB_STOP - (out.length % TAB_STOP)) : ch;
    }
    return out;
};
const isBlank = (line) => line.trim() === '';
/**
 * Some sources (web pages, word processors) put a blank line after every line
 * of text when copied. When no two content lines are adjacent, the single
 * blank lines are an artifact: drop them, and keep runs of two or more as one
 * stanza break.
 */
const collapseDoubleSpacing = (lines) => {
    const contentCount = lines.filter(line => !isBlank(line)).length;
    if (contentCount < 3)
        return lines;
    for (let i = 1; i < lines.length; i++) {
        if (!isBlank(lines[i]) && !isBlank(lines[i - 1]))
            return lines;
    }
    const out = [];
    let blankRun = 0;
    for (const line of lines) {
        if (isBlank(line)) {
            blankRun++;
            continue;
        }
        if (blankRun >= 2 && out.length > 0)
            out.push('');
        blankRun = 0;
        out.push(line);
    }
    return out;
};
/** Splits pasted text into clean lines (trailing whitespace removed). */
export const normalizeLines = (text) => {
    const lines = text
        .normalize('NFC')
        .replace(/\r\n?/g, '\n')
        .replace(ZERO_WIDTH, '')
        .replace(SPACE_LOOKALIKES, ' ')
        .replace(CHORD_SITE_MARKUP, '')
        .split('\n')
        .map(line => expandTabs(line).replace(CONTROL_CHARS, '').trimEnd());
    return collapseDoubleSpacing(lines);
};
/**
 * Typographic dashes and quotes → ASCII, one character for one, for matching
 * chord notation and structure. Lyrics are taken from the original line.
 */
export const toAsciiPunctuation = (line) => line.replace(DASHES, '-').replace(SINGLE_QUOTES, "'").replace(DOUBLE_QUOTES, '"');
